/**
 * Filtre les messages techniques backend (SQL, stack traces, exceptions Laravel,
 * chemins fichiers, etc.) et renvoie un message fonctionnel utilisateur.
 *
 * L'utilisateur final ne doit jamais voir : requêtes SQL, classes PHP, chemins
 * vendor/, traces, codes SQLSTATE, noms de tables/colonnes, ni les détails
 * d'environnement.
 */

export type ErrorLike =
	| string
	| {
			response?: { status?: number; data?: any };
			message?: string;
			_handled?: boolean;
			_permissionDenied?: boolean;
	  }
	| null
	| undefined;

const TECHNICAL_PATTERNS: RegExp[] = [
	/SQLSTATE/i,
	/SQL\s*syntax/i,
	/SQL\s*:/i,
	/Integrity\s+constraint\s+violation/i,
	/Duplicate\s+entry/i,
	/Foreign\s+key\s+constraint/i,
	/Unknown\s+column/i,
	/Column\s+.+\s+(doesn['’]t\s+exist|not\s+found)/i,
	/Table\s+.+\s+(doesn['’]t\s+exist|not\s+found)/i,
	/Database\s+.+\s+error/i,
	/Lock\s+wait\s+timeout/i,
	/Deadlock/i,
	/MySQL\s+server/i,
	/PDOException/i,
	/QueryException/i,
	/Eloquent/i,
	/Illuminate\\/,
	/Laravel/i,
	/Symfony\\/,
	/Stack\s+trace/i,
	/at\s+\/?(var|home|opt|app|vendor)\//i,
	/in\s+\/.+\.(php|js|ts):\d+/i,
	/\.php(:\d+)?/i,
	/Undefined\s+(property|variable|method|index|offset|array\s+key)/i,
	/Trying\s+to\s+(access|get\s+property|read\s+property)/i,
	/Call\s+to\s+undefined/i,
	/Class\s+.+\s+not\s+found/i,
	/TypeError/i,
	/ParseError/i,
	/FatalError/i,
	/ErrorException/i,
	/RuntimeException/i,
	/InvalidArgumentException/i,
	/Connection\s+(refused|reset|timed\s+out|aborted)/i,
	/Redis\s+(connection|error)/i,
	/ENOTFOUND|ECONNREFUSED|ETIMEDOUT|EAI_AGAIN/,
	/Cannot\s+(read|set)\s+propert(y|ies)/i,
	/null\s+pointer/i,
	/Bearer\s+token/i,
	/CSRF\s+token\s+mismatch/i,
];

export function isTechnicalMessage(msg: unknown): boolean {
	if (typeof msg !== "string" || !msg.trim()) return false;
	return TECHNICAL_PATTERNS.some((p) => p.test(msg));
}

export function functionalFallback(status?: number): string {
	switch (status) {
		case 400:
			return "Données invalides. Veuillez vérifier votre saisie.";
		case 401:
			return "Votre session a expiré. Veuillez vous reconnecter.";
		case 403:
			return "Vous n'avez pas l'autorisation pour effectuer cette action.";
		case 404:
			return "L'élément demandé est introuvable.";
		case 409:
			return "Cette opération entre en conflit avec une donnée existante.";
		case 413:
			return "Le fichier envoyé est trop volumineux.";
		case 422:
			return "Données invalides. Veuillez vérifier votre saisie.";
		case 429:
			return "Trop de tentatives. Veuillez patienter quelques instants.";
		case 500:
			return "Une erreur est survenue, veuillez réessayer plus tard.";
		case 502:
		case 503:
		case 504:
			return "Le service est momentanément indisponible. Veuillez réessayer.";
		default:
			return "Une erreur est survenue. Veuillez réessayer.";
	}
}

function pickValidationMessage(data: any): string | undefined {
	// 3 formes connues côté API :
	//   1. data.errors                  (Laravel par défaut)
	//   2. data.data.errors             (réponses wrappées { response, data: { errors } })
	//   3. data.data = { field: [msg] } (ResponseException jette $validator->errors()
	//      directement dans `data` du wrap, sans clé `errors` intermédiaire)
	const candidates = [data?.errors, data?.data?.errors, data?.data];
	for (const errors of candidates) {
		if (!errors || typeof errors !== "object") continue;
		// `data.data` peut contenir des clés non-validation (signature, code,
		// response wrappant) — on prend la 1ʳᵉ valeur qui ressemble à un
		// message de validation (string ou array de strings).
		for (const key of Object.keys(errors)) {
			if (key === "response" || key === "signature" || key === "code") continue;
			const first = errors[key];
			const msg = Array.isArray(first) ? first[0] : first;
			if (typeof msg === "string" && !isTechnicalMessage(msg)) return msg;
		}
	}
	return undefined;
}

/**
 * Renvoie un message fonctionnel propre à afficher à l'utilisateur.
 * Aucun détail technique ne doit transparaître.
 */
export function sanitizeErrorMessage(input: ErrorLike): string {
	if (input == null) return functionalFallback();

	if (typeof input === "string") {
		return isTechnicalMessage(input) ? functionalFallback() : input;
	}

	const status = input.response?.status;
	const data = input.response?.data;

	const validationMsg = pickValidationMessage(data);
	if (validationMsg) return validationMsg;

	const candidates = [
		data?.message,
		data?.data?.message,
		data?.data?.signature,
		input.message,
	];

	for (const candidate of candidates) {
		if (typeof candidate === "string" && candidate.trim() && !isTechnicalMessage(candidate)) {
			return candidate;
		}
	}

	return functionalFallback(status);
}
